import { PLUGIN_ICON, getMenuIcon } from '#pplx/frontend/apps/ask/skills/utils/getMenuIcon.js';
import { getPluginMentionURL } from '#pplx/frontend/apps/ask/skills/utils/getPluginMentionURL.js';
import { getSkillMentionTarget, getSkillMentionURL, } from '#pplx/frontend/apps/ask/skills/utils/getSkillMentionURL.js';
import { escapeMarkdownLinkText, getMarkdownCodeRanges, MARKDOWN_LINK_REGEX, unescapeMarkdownLinkText, } from '#pplx/frontend/common/markdownTitle.js';
export function createQueryPrefillTextNode(text) {
    return {
        detail: 0,
        format: 0,
        mode: 'normal',
        style: '',
        text,
        type: 'text',
        version: 1,
    };
}
export function createQueryPrefillEditorState(children) {
    const paragraph = {
        children,
        direction: 'ltr',
        format: '',
        indent: 0,
        textFormat: 0,
        textStyle: '',
        type: 'paragraph',
        version: 1,
    };
    return {
        root: {
            children: [paragraph],
            direction: 'ltr',
            format: '',
            indent: 0,
            type: 'root',
            version: 1,
        },
    };
}
function createLinkNode(localizedName, rawTarget) {
    const targetMatch = /^(\S+?)(?:\s+"((?:\\.|[^"])*)")?$/.exec(rawTarget);
    const url = (targetMatch?.[1] ?? rawTarget).replace(/\\([\\()])/g, '$1');
    const title = targetMatch?.[2]?.replace(/\\([\\()"])/g, '$1') ?? null;
    return {
        children: [createQueryPrefillTextNode(localizedName)],
        direction: null,
        format: '',
        indent: 0,
        rel: null,
        target: null,
        title,
        type: 'link',
        url,
        version: 1,
    };
}
export function createSkillMention({ localizedName, skillId, skillName, }) {
    const skillUrl = getSkillMentionURL({ id: skillId });
    return {
        markdown: `[${escapeMarkdownLinkText(localizedName)}](${skillUrl})`,
        node: {
            props: {
                uuid: skillId,
                variant: 'skills',
                text: skillName,
                localizedName,
                prefixWithSlash: false,
                url: skillUrl,
                icon: { type: 'icon', icon: getMenuIcon(skillName) },
            },
            type: 'mention',
            version: 1,
        },
    };
}
export function createPluginMentionNode({ displayName, name, pluginId, }) {
    return {
        props: {
            uuid: pluginId,
            variant: 'plugin',
            text: name,
            localizedName: displayName,
            prefixWithSlash: false,
            url: getPluginMentionURL(pluginId),
            icon: { type: 'icon', icon: PLUGIN_ICON },
        },
        type: 'mention',
        version: 1,
    };
}
export function buildSkillPrefill({ afterMention, beforeMention, localizedName, skillId, skillName, }) {
    const { markdown: skillMarkdown, node: skillNode } = createSkillMention({
        localizedName,
        skillId,
        skillName,
    });
    const children = [];
    if (beforeMention)
        children.push(createQueryPrefillTextNode(beforeMention));
    children.push(skillNode);
    if (afterMention)
        children.push(createQueryPrefillTextNode(afterMention));
    return {
        value: `${beforeMention}${skillMarkdown}${afterMention}`,
        json: createQueryPrefillEditorState(children),
    };
}
export function buildSkillLinkPrefill(value) {
    const linkRegex = new RegExp(MARKDOWN_LINK_REGEX);
    const codeRanges = getMarkdownCodeRanges(value);
    const children = [];
    let mentionCount = 0;
    let markdown = '';
    let lastIndex = 0;
    let match;
    while ((match = linkRegex.exec(value)) !== null) {
        const [rawLink, rawLocalizedName, rawUrl] = match;
        if (!rawLocalizedName || !rawUrl)
            continue;
        const matchIndex = match.index;
        if (codeRanges.some((range) => matchIndex >= range.start && matchIndex < range.end))
            continue;
        const target = getSkillMentionTarget(rawUrl);
        const localizedName = unescapeMarkdownLinkText(rawLocalizedName);
        const beforeMention = value.slice(lastIndex, matchIndex);
        if (beforeMention)
            children.push(createQueryPrefillTextNode(beforeMention));
        markdown += beforeMention;
        if (!target?.skillIdFromUrl) {
            children.push(createLinkNode(localizedName, rawUrl));
            markdown += rawLink;
            lastIndex = matchIndex + rawLink.length;
            continue;
        }
        const mention = createSkillMention({
            localizedName,
            skillId: target.skillIdFromUrl,
            skillName: target.skillIdFromUrl,
        });
        children.push(mention.node);
        mentionCount += 1;
        markdown += mention.markdown;
        lastIndex = matchIndex + rawLink.length;
    }
    if (mentionCount === 0)
        return null;
    const trailingText = value.slice(lastIndex);
    if (trailingText)
        children.push(createQueryPrefillTextNode(trailingText));
    return {
        value: `${markdown}${trailingText}`,
        json: createQueryPrefillEditorState(children),
    };
}
//# sourceMappingURL=buildSkillQueryPrefill.js.map